/**
 * Running-balance-over-time chart for a rebuilt ATO RBA ledger (MOR-587).
 *
 * Plots the rebuilt closing balance at each period end from the cleaned
 * `atoLedgerEntries` movement rows. The ATO's own balance column is unreliable
 * (stamped on process date), so this uses the deterministically recomputed
 * `rebuiltBalance`. Reuses the shared ChartContainer (recharts) foundation.
 */

import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import { cn } from "@/utils/misc";

type Granularity = "day" | "week" | "month" | "quarter" | "year";

interface LedgerRow {
  effectiveDate: string;
  rebuiltBalance: number;
  kind: string;
}

const GRANULARITIES: Granularity[] = [
  "day",
  "week",
  "month",
  "quarter",
  "year",
];

const config = {
  balance: { label: "Balance owing", color: "hsl(var(--chart-1))" },
} satisfies ChartConfig;

function isoWeekKey(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return iso;
  const day = (d.getUTCDay() + 6) % 7; // Mon=0
  d.setUTCDate(d.getUTCDate() - day + 3); // nearest Thursday
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week =
    1 +
    Math.round(
      ((d.getTime() - firstThursday.getTime()) / 86400000 -
        3 +
        ((firstThursday.getUTCDay() + 6) % 7)) /
        7,
    );
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

function periodKey(iso: string, gran: Granularity): string {
  const [y, m] = iso.split("-");
  if (!y) return iso;
  if (gran === "year") return y;
  if (gran === "quarter") return `${y}-Q${Math.floor((Number(m) - 1) / 3) + 1}`;
  if (gran === "month") return `${y}-${m ?? "01"}`;
  if (gran === "week") return isoWeekKey(iso);
  return iso;
}

const dollar = (v: number) =>
  `${v < 0 ? "-" : ""}$${Math.abs(v / 1000).toFixed(v >= 100000 ? 0 : 1)}k`;

export function RunningBalanceChart({
  rows,
  atoInsolvencyDate,
  caseInsolvencyDate,
}: {
  rows: LedgerRow[];
  /** ATO-only estimated insolvency date, derived from the RBA (YYYY-MM-DD). */
  atoInsolvencyDate?: string;
  /** Case-wide estimated insolvency date (other indicators), to overlay. */
  caseInsolvencyDate?: string;
}) {
  const [granularity, setGranularity] = useState<Granularity>("quarter");

  const data = useMemo(() => {
    // Rows arrive ordered by effective date; the last movement in a period
    // carries that period's closing balance.
    const byPeriod = new Map<string, number>();
    for (const r of rows) {
      if (r.kind !== "movement" || !r.effectiveDate) continue;
      byPeriod.set(periodKey(r.effectiveDate, granularity), r.rebuiltBalance);
    }
    return Array.from(byPeriod.entries()).map(([period, balance]) => ({
      period,
      balance,
    }));
  }, [rows, granularity]);

  // Snap a date to the nearest plotted period (the X axis is categorical, so a
  // reference line must sit on a period that exists in the data).
  const snap = useMemo(() => {
    return (date?: string): string | null => {
      if (!date || data.length === 0) return null;
      const target = periodKey(date, granularity);
      if (data.some((d) => d.period === target)) return target;
      const after = data.find((d) => d.period >= target);
      return (after ?? data[data.length - 1]).period;
    };
  }, [data, granularity]);

  const atoPeriod = snap(atoInsolvencyDate);
  const casePeriod = snap(caseInsolvencyDate);

  if (data.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Balance over time
        </p>
        <div className="flex gap-0.5 rounded-md bg-muted/50 p-0.5">
          {GRANULARITIES.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularity(g)}
              className={cn(
                "rounded px-2 py-0.5 text-[11px] capitalize transition-colors",
                granularity === g
                  ? "bg-background font-medium shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {g}
            </button>
          ))}
        </div>
      </div>
      {/* width:0 + min-width:100% stops recharts' ResponsiveContainer from
          reporting a large intrinsic width, which would otherwise expand the
          Radix ScrollArea's `display:table` viewport and clip the whole panel. */}
      <div
        className="w-full overflow-x-hidden"
        style={{ width: 0, minWidth: "100%" }}
      >
        <ChartContainer
          config={config}
          className="aspect-auto h-[260px] w-full"
        >
          <AreaChart data={data} margin={{ left: 4, right: 8, top: 16 }}>
            <defs>
              <linearGradient id="ato-balance-fill" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-balance)"
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-balance)"
                  stopOpacity={0.02}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              stroke="hsl(var(--border))"
              strokeOpacity={0.5}
            />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              tickFormatter={dollar}
              tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(v) =>
                    typeof v === "number"
                      ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : String(v)
                  }
                />
              }
            />
            <Area
              dataKey="balance"
              type="stepAfter"
              stroke="var(--color-balance)"
              strokeWidth={2}
              fill="url(#ato-balance-fill)"
            />
            {casePeriod && casePeriod !== atoPeriod && (
              <ReferenceLine
                x={casePeriod}
                stroke="hsl(var(--muted-foreground))"
                strokeDasharray="3 3"
                strokeOpacity={0.55}
                label={{
                  value: "Other indicators",
                  position: "insideTopRight",
                  fontSize: 9,
                  fill: "hsl(var(--muted-foreground))",
                }}
              />
            )}
            {atoPeriod && (
              <ReferenceLine
                x={atoPeriod}
                stroke="#4f46e5"
                strokeWidth={1.5}
                strokeDasharray="5 3"
                label={{
                  value: "Est. insolvency (ATO)",
                  position: "insideTopLeft",
                  fontSize: 10,
                  fontWeight: 600,
                  fill: "#4f46e5",
                }}
              />
            )}
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  );
}
